import type { I_ShadowParams } from '@/types/ShadowDom';

/**
 * Creates base style reset CSS to prevent host page styles from leaking into shadow DOM
 * @param rootId - The ID for the widget root element
 * @returns CSS string with base style reset
 */
function createBaseStyleReset(rootId: string): string {
  return `
    :host {
      display: block;
      position: relative;
      z-index: 99999999999;
    }
    #${rootId} {
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    #${rootId} * {
    }
  `;
}

function injectBaseStyleReset({ shadowRoot, rootId }: I_ShadowParams): void {
  const baseResetStyle = document.createElement('style');
  baseResetStyle.textContent = createBaseStyleReset(rootId);
  shadowRoot.appendChild(baseResetStyle);
}

/**
 * Finds the widget's style tag in the document
 * @param rootId - The ID for the widget root element
 * @returns The style element if found, null otherwise
 */
function findWidgetStyleTag(rootId: string): HTMLStyleElement | null {
  // First, try to find the injected style tag with the widget styles
  const styleTag = document.getElementById('enable-widget-styles') as HTMLStyleElement | null;

  if (styleTag && styleTag.textContent) {
    return styleTag;
  }

  // Fallback: try to find any style tag that might contain our styles
  // This handles cases where the style might be injected differently
  const allStyles = document.querySelectorAll('style');
  for (const style of allStyles) {
    if (style.textContent && style.textContent.includes(`#${rootId}`)) {
      return style as HTMLStyleElement;
    }
  }

  return null;
}

function injectWidgetStyles({ shadowRoot, rootId }: I_ShadowParams) {
  const styleTag = findWidgetStyleTag(rootId);

  if (styleTag && styleTag.textContent) {
    const shadowStyle = document.createElement('style');
    shadowStyle.textContent = styleTag.textContent;
    shadowRoot.appendChild(shadowStyle);
    return true;
  }

  return false;
}

/**
 * Injects all styles (base reset + widget styles) into shadow DOM
 */
export function injectStylesIntoShadow(params: I_ShadowParams): void {
  const { shadowRoot } = params;

  // Inject base style reset first to prevent host page styles from leaking
  injectBaseStyleReset(params);

  // Function to inject widget styles
  const injectStyles = () => {
    return injectWidgetStyles(params);
  };

  // Try to inject styles immediately
  if (!injectStyles()) {
    // If styles aren't found, wait a bit and try again (handles async injection)
    // This is a fallback for edge cases where CSS injection might be delayed
    setTimeout(() => {
      if (!shadowRoot.querySelector(`style:not(:first-of-type)`)) {
        injectStyles();
      }
    }, 0);
  }
}
